import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation} from '@angular/core';
import {MultiSelect} from 'primeng/primeng';
import {IPriorityDropdownOptionValue, PriorityDropdownOption} from '../../models/IPriorityDropdownOption';

@Component({
  selector: 'app-priority-dropdown',
  templateUrl: './priority-dropdown.component.html',
  styleUrls: ['./priority-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PriorityDropdownComponent implements OnInit {

  private _firstLoad = true;
  private _availableOptions: PriorityDropdownOption[] = [];
  private _originalOptions: PriorityDropdownOption[] = [];
  private _autoSelectId: number;
  private _autoMultiSelectIds: number[];
  private _manualSelectedAllOnLoad = false;
  selectedOptions: IPriorityDropdownOptionValue[] = [];
  selectedOption: IPriorityDropdownOptionValue;
  @Input() maxHeight = '400px';
  @Input() singleSelection = false;
  @Input() showDates = true;
  @Input() defaultLabel = 'Select Items';
  @Input() selectedItemsLabel = '{0} Selected';
  @Input() selectAllItemsLabel = 'Select All';
  @Input() allItemsSelectedLabel = 'All Items Selected';
  @Input() styleOverrides: {} = null;
  @Input() styleClassAppends: string[] = null;
  @Input() noDefaultSelection = false;
  @Input() forceDisable = false;
  @Output() valueChangedCallback = new EventEmitter<IPriorityDropdownOptionValue[]|IPriorityDropdownOptionValue>();

  @ViewChild('priorityDropdownSelect') private multiselect: MultiSelect;

  constructor() { }

  ngOnInit() {
    this._firstLoad = true;
  }

  get availableOptions(): PriorityDropdownOption[] {
    return this._availableOptions;
  }

  get disabled() {
    return (!this.availableOptions || this.availableOptions.length === 0 ||
        (this.availableOptions.length === 1 && this.availableOptions[0].value.primaryId === -1 && !this.singleSelection)
                    || this.forceDisable);
  }

  get singleSelectPanelStyleClasses(): string {
    return `selected-initiative single-select ${(this.styleClassAppends ? this.styleClassAppends.join(' ') : '')}`;
  }

  get multiSelectPanelStyleClasses(): string {
    return `selected-initiative multi-select ${(this.styleClassAppends ? this.styleClassAppends.join(' ') : '')}`;
  }

  @Input('availableOptions')
  set availableOptions(options: PriorityDropdownOption[]) {
    if (options && options.length > 0) {
      this._originalOptions = options;
      this._availableOptions = this.singleSelection ?
                                options :
                                [this.generateSelectAllRow(options ? options.length : 0), ...options];
      if (this._firstLoad) {
        this._firstLoad = false;
        if (this.noDefaultSelection && !this._manualSelectedAllOnLoad) {
          this.deselectEverything();
        } else {
          // forces all selection on data load for multiselect
          this.deselectRegularOptions();
        }
      }

      if (this._autoSelectId) {
        this.executeAutoSelect();
      }
    }
  }

  selectItemWithID(id: number) {
    this._autoSelectId = id;
    this.executeAutoSelect();
  }

  executeAutoSelect() {
    if (this._availableOptions && this._availableOptions.length > 0) {
      const matchingOption = this._availableOptions.find((option: PriorityDropdownOption) => {
        return option.value.primaryId === this._autoSelectId;
      });
      if (matchingOption) {
        setTimeout(() => {
          this.selectedOption = matchingOption.value;
          this.selectedOptions = [matchingOption.value];
          this._autoSelectId = null;
          if (this.valueChangedCallback) {
            this.valueChangedCallback.emit(this.selectedOption);
          }
        });
      }
    }
  }

  selectItemsWithIds(ids: number[]) {
    this._autoMultiSelectIds = ids;
    this.executeAutoMultiSelect();
  }

  executeAutoMultiSelect() {
    if (this._availableOptions && this._availableOptions.length > 0) {
      const matchingOptions: PriorityDropdownOption[] = this._availableOptions.filter((option: PriorityDropdownOption) => {
        return this._autoMultiSelectIds.indexOf(option.value.primaryId) >= 0;
      });
      if (matchingOptions && matchingOptions.length > 0) {
        setTimeout(() => {
          const matchingOptionIds = matchingOptions.map((option: PriorityDropdownOption) => {
            return option.value;
          });
          this.selectedOptions = matchingOptionIds;
          this._autoMultiSelectIds = null;
          if (this.valueChangedCallback) {
            this.valueChangedCallback.emit(this.selectedOptions);
          }
        });
      }
    }
  }

  clearSelections() {
    this.selectedOptions = [];
  }

  selectAll() {
    const selectAll = this.generateSelectAllRow(this._originalOptions ? this._originalOptions.length : 0).value;
    this.onSelectOption({itemValue: selectAll});
    this.onPanelHide();
  }

  manualSelectAll() {
    this._manualSelectedAllOnLoad = true;
    this.selectAll();
  }

  onSelectOption(event) {
    if (event.itemValue.primaryId === -1) {
      this.deselectRegularOptions();
    } else {
      this.deselectAllOption();
    }
    if (this.valueChangedCallback) {
      this.valueChangedCallback.emit(this.selectedOptions);
    }
  }

  onSelectSingleOption(event) {
    this.selectedOption = event.value;
    if (this.valueChangedCallback) {
      this.valueChangedCallback.emit(this.selectedOption);
    }
  }

  deselectRegularOptions() {
    if (this.singleSelection || !this.multiselect) {
      // don't let this execute in single selection mode
      return;
    }
    this.multiselect.displaySelectedLabel = false;
    const countVal = this.availableOptions.length > 1 ? this.availableOptions.length - 1 : 0;
    this.multiselect.defaultLabel = this.allItemsSelectedLabel.replace('{0}', `${countVal}`);
    this.selectedOptions = [this.generateSelectAllRow(countVal).value];
  }

  deselectAllOption() {
    this.multiselect.displaySelectedLabel = true;
    this.multiselect.defaultLabel = this.defaultLabel;
    this.multiselect.selectedItemsLabel = this.selectedItemsLabel;
    this.selectedOptions = this.selectedOptions.filter((item: IPriorityDropdownOptionValue) => {
      return item.primaryId > -1;
    });
  }

  deselectEverything() {
    if (this.singleSelection || !this.multiselect) {
      // don't let this execute in single selection mode
      setTimeout(() => {
        this.deselectEverything();
      }, 1);
      return;
    }
    this.multiselect.displaySelectedLabel = true;
    this.multiselect.defaultLabel = this.defaultLabel;
    this.multiselect.selectedItemsLabel = this.selectedItemsLabel;
    this.selectedOptions = [];
  }

  get initiativeDropdownInlineStyles() {
    const baseStyles = {
      width: '700px',
      margin: 0,
      padding: '0 16px 0 0',
      height: '38px',
      lineHeight: '36px',
      border: '1px solid #706F6F',
      borderRadius: '4px',
      fontSize: '14px',
      backgroundColor: this.disabled ? '#E6E6E6' : 'transparent'
    };
    if (!this.styleOverrides) {
      if (this.singleSelection) {
        return baseStyles;
      } else {
        return {...baseStyles, lineHeight: '30px'};
      }
    } else {
      const modifiedStyles = baseStyles;
      modifiedStyles.width = this.styleOverrides['width'] ? this.styleOverrides['width'] : baseStyles.width;
      modifiedStyles.height = this.styleOverrides['height'] ? this.styleOverrides['height'] : baseStyles.height;
      modifiedStyles.lineHeight = this.styleOverrides['lineHeight'] ? this.styleOverrides['lineHeight'] : baseStyles.lineHeight;
      modifiedStyles.fontSize = this.styleOverrides['fontSize'] ? this.styleOverrides['fontSize'] : baseStyles.fontSize;
      if (this.singleSelection) {
        return modifiedStyles;
      } else {
        return {...modifiedStyles, lineHeight: '30px'};
      }
    }
  }

  hasDate(item: PriorityDropdownOption): boolean {
    return item.hasDate;
  }

  onPanelHide() {
    if (!this.multiselect) {
      return;
    }
    this.multiselect.panel.firstElementChild.scrollTop = 0;
  }

  onPanelShow() {
    if (!this.multiselect) {
      return;
    }
    this.multiselect.panel.firstElementChild.scrollTop = 0;
  }

  generateSelectAllRow(totalCount: number): PriorityDropdownOption {
    const label = this.allItemsSelectedLabel.replace('{0}', `${totalCount}`);
    return new PriorityDropdownOption({
      label: label,
      title: this.selectAllItemsLabel,
      value: {
        htmlContent: `<strong>${this.selectAllItemsLabel}</strong>`,
        value: -1,
        primaryId: -1,
        colorCode: '#efefef',
        planId: 1,
        startDate: '',
        endDate: '',
        parentIds: {
          planId: 1
        }
      }, styleClass: 'ui-multiselect-item'
    });
  }
}
